/**
 * Configures axios to send JWT access token, handle 401 by refreshing, and
 * on 404 for get-user/get-user-by-id clear storage and redirect to login.
 * Import this once at app startup (e.g. index.js).
 */

import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isPublicAuthUrl,
} from "../utils/authTokens";
import {
  handleImpersonationExpiry,
  isImpersonating,
} from "../utils/impersonation";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || "";

let refreshPromise = null;

function logoutToLogin() {
  clearTokens();
  const user = localStorage.getItem("user");
  const organization = localStorage.getItem("organization");
  if (user || organization) {
    localStorage.removeItem("user");
    localStorage.removeItem("organization");
    window.location.href = "/login";
  }
}

function doRefresh() {
  if (refreshPromise) return refreshPromise;
  const refresh_token = getRefreshToken();
  if (!refresh_token) {
    if (isImpersonating()) {
      handleImpersonationExpiry();
      return Promise.reject(new Error("Impersonation session expired"));
    }
    return Promise.reject(new Error("No refresh token"));
  }
  refreshPromise = axios
    .post(BASE_URL + "auth/refresh", { refresh_token })
    .then((res) => {
      const { access_token, refresh_token: newRefresh, expires_in } = res.data;
      setTokens({
        access_token,
        refresh_token: newRefresh,
        expires_in,
      });
      refreshPromise = null;
      return access_token;
    })
    .catch((err) => {
      refreshPromise = null;
      if (err.response && err.response.status === 401) {
        if (isImpersonating()) {
          console.info("[Auth] Impersonation refresh failed; restoring admin session.");
          handleImpersonationExpiry();
        } else {
          console.info("[Auth] Logging out: token refresh failed with 401 (session expired or invalid refresh token).");
          logoutToLogin();
        }
      }
      throw err;
    });
  return refreshPromise;
}

axios.interceptors.request.use(
  (config) => {
    const url = config.url || (config.baseURL && config.url ? config.baseURL + config.url : "");
    const fullUrl = url.startsWith("http") ? url : (config.baseURL || BASE_URL) + (config.url || "");
    if (!isPublicAuthUrl(fullUrl)) {
      const token = getAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (err) => Promise.reject(err)
);

function getFullUrl(config) {
  const url = config.url || "";
  return url.startsWith("http")
    ? url
    : (config.baseURL || BASE_URL) + url;
}

axios.interceptors.response.use(
  (response) => response,
  async (err) => {
    const originalRequest = err.config;
    const status = err.response?.status;
    const fullUrl = getFullUrl(originalRequest);

    // 404 on get-user or get-user-by-id: user no longer exists; clear storage and redirect to login (unless already on login)
    if (status === 404 && fullUrl.includes("get-user")) {
      if (isImpersonating()) {
        console.info("[Auth] get-user 404 during impersonation; restoring admin session.");
        handleImpersonationExpiry();
        return Promise.reject(err);
      }
      console.info("[Auth] Logging out: get-user returned 404 (user no longer exists).");
      clearTokens();
      localStorage.removeItem("user");
      localStorage.removeItem("organization");
      const pathname = typeof window !== "undefined" ? window.location.pathname : "";
      if (!pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
      return Promise.reject(err);
    }

    if (status !== 401) {
      return Promise.reject(err);
    }

    if (isPublicAuthUrl(fullUrl)) {
      return Promise.reject(err);
    }

    if (fullUrl.includes("auth/refresh")) {
      return Promise.reject(err);
    }

    if (originalRequest._retryAfterRefresh) {
      return Promise.reject(err);
    }

    try {
      await doRefresh();
      originalRequest._retryAfterRefresh = true;
      const token = getAccessToken();
      if (token) {
        originalRequest.headers.Authorization = `Bearer ${token}`;
      }
      return axios(originalRequest);
    } catch (refreshErr) {
      return Promise.reject(refreshErr.response ? refreshErr : err);
    }
  }
);
